import { Component, EventEmitter, Input, Output, Signal, signal, WritableSignal } from '@angular/core';
import { Experiences } from '@local/client-contracts';
import { cloneDeep, isEqual } from 'lodash';
import { assistantContent } from '../../helpers/assistant.content';
import { WritingAssistantService } from 'src/app/bar/services/writing-assistant.service';
import { AssistantDetails } from 'src/app/bar/views/chat-page/services/chats.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'general-preferences',
  templateUrl: './general-preferences.component.html',
  styleUrls: ['./general-preferences.component.scss'],
})
export class GeneralPreferencesComponent {
  readonly MAX_CONVERSATIONS: number = 3;
  readonly assistantContent = assistantContent;
  assistantId: string;
  readonly: boolean;
  displayOnChatPage: WritableSignal<boolean> = signal(true);
  displayOnQuickSearch: WritableSignal<boolean> = signal(false);
  displayNoPromptMessage: WritableSignal<boolean> = signal(false);
  displaySubtitle: WritableSignal<boolean> = signal(true);

  viewOnly: boolean;
  generalProperties: Experiences.ExperienceGeneralSettings;
  conversation: WritableSignal<string> = signal('');
  conversationStarterList: WritableSignal<string[]> = signal(['']);
  defaultAssistant: Signal<AssistantDetails>;

  @Input() isAssistantEditable = false;
  @Input() isViewer: boolean;
  @Input() set assistant(val: Experiences.ExperienceItem<'general'>) {
    const assistantChanged = !this.assistantId || val?.id !== this.assistantId;
    if (assistantChanged || !isEqual(val?.settings, this.generalProperties)) {
      this.assistantId = val.id;
      this.readonly = val.readOnly;
      this.viewOnly = val.permissionRole === 'viewer';

      if (val.settings?.general) {
        this.generalProperties = cloneDeep(val.settings || {});
        this.conversationStarterList.set(this.generalProperties?.general?.conversationStarterMessages || ['']);
        this.displayNoPromptMessage.set(this.shouldDisplayNoPromptMessage());
        this.displaySubtitle.set(this.shouldDisplaySubtitle());
        this.initialPreferences();
      }
    }
  }

  @Output() updateProperties = new EventEmitter<Experiences.ExperienceGeneralSettings>();

  constructor(private writingAssistantService: WritingAssistantService) {
    this.defaultAssistant = toSignal(this.writingAssistantService.defaultAssistant$);
  }

  initialPreferences() {
    this.displayOnChatPage.set(this.generalProperties?.general?.displayOnChatPage != false ? true : false);
    this.displayOnQuickSearch.set(!!this.generalProperties?.general?.displayOnQuickSearch);
  }
  updateAssistantProperties(generalPreferences?: Experiences.GeneralExtendSettings) {
    this.generalProperties = {
      ...this.generalProperties,
      general: {
        ...this.generalProperties?.general,
        ...(generalPreferences || {}),
      },
    };
    this.updateProperties.emit(this.generalProperties);
  }
  toggleDisplayOnChatPage(event: { checked: boolean }): void {
    const isChecked = event.checked;
    if (!isChecked && this.displayOnQuickSearch()) {
      this.displayOnQuickSearch.set(false);
    }
    this.displayOnChatPage.set(isChecked);
    this.updateAssistantProperties({
      displayOnChatPage: this.displayOnChatPage(),
      displayOnQuickSearch: this.displayOnQuickSearch(),
      knowledgeType: this.generalProperties?.general?.knowledgeType,
    });
  }
  toggleDisplayOnQuickSearch(event: { checked: boolean }): void {
    this.displayOnQuickSearch.set(event.checked);
    this.updateAssistantProperties({
      displayOnQuickSearch: this.displayOnQuickSearch(),
      knowledgeType: this.generalProperties?.general?.knowledgeType,
    });
  }

  onUpdateConversationsList(starters) {
    starters = starters.filter((s) => !!s);
    this.updateAssistantProperties({
      knowledgeType: this.generalProperties?.general?.knowledgeType,
      conversationStarterMessages: starters,
    });
  }

  shouldDisplaySubtitle(): boolean {
    const list = this.conversationStarterList();
    return list?.length > 1 || (list?.length === 1 && list[0] !== '') || !this.viewOnly;
  }

  shouldDisplayNoPromptMessage(): boolean {
    const list = this.conversationStarterList();
    return this.viewOnly && (list?.length === 0 || (list?.length === 1 && list[0] === ''));
  }
}
